import * as React from 'react';
import {
  Box,
  Button,
  ParagraphText,
  StackItem,
  StackLayout,
  UtilityText,
} from '@leagueplatform/genesis-core';
import { getMasonryDriver } from '@leagueplatform/masonry';
import { MasonryEngine } from '@leagueplatform/masonry-engine';

export const CONTENT_MAX_WIDTH = '1272px';

export const MasonryContainerView = () => {
  const [masonryNodeId, setMasonryNodeId] = React.useState('');
  const [masonryDriverQueryParams, setMasonryDriverQueryParams] =
    React.useState<{ [k in string]: string }>({});

  const queryParams = Object.keys(masonryDriverQueryParams);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const nodeId = formData.get('nodeId') as string;
    setMasonryNodeId(nodeId);
  };

  const handleAddQueryParam = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const key = formData.get('queryParamKey') as string;
    const value = formData.get('queryParamValue') as string;

    if (key && value) {
      setMasonryDriverQueryParams((prev) => ({
        ...prev,
        [key]: value,
      }));
      // Reset form
      e.currentTarget.reset();
    }
  };

  const handleRemoveQueryParam = React.useCallback((key: string) => {
    setMasonryDriverQueryParams((prev) => {
      const curr = { ...prev };
      delete curr[key];
      return curr;
    });
  }, []);

  const MasonryContainerDriver = React.useCallback(
    () =>
      getMasonryDriver({
        rootNodeId: masonryNodeId,
        queryParams: masonryDriverQueryParams,
      }),
    [masonryNodeId, masonryDriverQueryParams],
  );

  return (
    <StackLayout css={{ gap: '$two', width: '100%' }}>
      <StackLayout>
        <Box
          css={{
            display: 'flex',
            width: '100%',
            justifyContent: 'center',
            marginY: '$two',
            flexDirection: 'column',
            gap: '$two',
          }}
        >
          <form onSubmit={handleSubmit}>
            <input
              type="text"
              name="nodeId"
              placeholder="Enter Masonry Node ID"
            />
            <button type="submit">View Masonry Container</button>
          </form>

          <form onSubmit={handleAddQueryParam}>
            <input
              type="text"
              name="queryParamKey"
              placeholder="Enter Query Param Key"
            />
            <input
              type="text"
              name="queryParamValue"
              placeholder="Enter Query Param Value"
            />
            <button type="submit">Add Query Param</button>
          </form>
        </Box>

        <StackItem>
          <ParagraphText>Query Params:</ParagraphText>
          {queryParams &&
            queryParams.map((p) => (
              <div
                key={p}
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <UtilityText>{`${p}: ${masonryDriverQueryParams[p]}`}</UtilityText>
                <Button
                  onClick={() => handleRemoveQueryParam(p)}
                  size="small"
                  icon="interfaceClose"
                  quiet
                  hideLabel
                >
                  Remove param
                </Button>
              </div>
            ))}
        </StackItem>
      </StackLayout>

      {masonryNodeId
        ? React.createElement(
            ({ children }: React.PropsWithChildren<{}>) => (
              <Box
                css={{
                  width: '100%',
                  borderTop: '2px solid black',
                  marginX: 'auto',
                  paddingInline: '$oneAndHalf',
                  '@mobile': {
                    paddingInline: '$two',
                  },
                  maxWidth: `var(--content-max-width, ${CONTENT_MAX_WIDTH})`,
                }}
              >
                {children}
              </Box>
            ),
            null,
            <MasonryEngine driver={MasonryContainerDriver()} />,
          )
        : null}
    </StackLayout>
  );
};
